import type { CompanyData } from '@/modules/auth/helpers/CompanyStorage';
import type { CompanyPermission, Role } from './PermissionManager';
import { roleHasPermission } from './permissionResolver';

const PERMISSION_ACTIONS = {
  READ: 'read',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
} as const;

const isGlobalRoleRecord = (role: Partial<Role>): boolean =>
  role?.role_name === 'global' ||
  role?.role_level === 'global' ||
  role?.role_name === 'global_admin';

/** Local copy to avoid circular import via PermissionManager at module load. */
const hasGlobalRoleScope = (
  globalRoles: Array<Partial<Role>> = [],
  selectedCompany?: { company_id?: string; roles?: Array<Partial<Role>> } | null,
  hasGlobalRoleFlag?: boolean
): boolean => {
  if (hasGlobalRoleFlag === true) {
    return true;
  }

  if (selectedCompany?.company_id === 'global') {
    return true;
  }

  if (Array.isArray(globalRoles) && globalRoles.some(isGlobalRoleRecord)) {
    return true;
  }

  const companyRoles: Array<Partial<Role>> = Array.isArray(selectedCompany?.roles)
    ? selectedCompany?.roles ?? []
    : [];

  return companyRoles.some(isGlobalRoleRecord);
};

export interface CompanyPermissionSummary {
  canRead: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

export interface ResolveCompanyPermissionsInput {
  companies: CompanyData[];
  companyId?: string | null;
  resource: string;
  globalRoles?: Role[];
  selectedCompany?: CompanyPermission | null;
  hasGlobalRole?: boolean;
  /** Login-selected company — fallback read when filter is all/empty (optional) */
  loginCompanyId?: string | null;
}

export interface ResolveCompanyPermissionsResult extends CompanyPermissionSummary {
  effectiveCompanyId: string | null;
  isGlobalScope: boolean;
  summary: CompanyPermissionSummary;
}

const FULL_ACCESS: CompanyPermissionSummary = {
  canRead: true,
  canCreate: true,
  canUpdate: true,
  canDelete: true,
};

const NO_ACCESS: CompanyPermissionSummary = {
  canRead: false,
  canCreate: false,
  canUpdate: false,
  canDelete: false,
};

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const normalizePermissionCompanyId = (value: unknown): string => {
  if (value == null) return '';
  return String(value).trim();
};

export const isSpecificPermissionCompanyId = (companyId?: string | null): boolean => {
  const normalized = normalizePermissionCompanyId(companyId);
  return Boolean(normalized) && normalized !== 'all' && normalized !== 'global';
};

export const isUuidCompanyId = (companyId?: string | null): boolean => {
  const normalized = normalizePermissionCompanyId(companyId);
  return UUID_PATTERN.test(normalized);
};

/** Check whether any role on a company grants resource + action (supports action aliases). */
export const companyHasResourceAction = (
  company: CompanyData,
  resource: string,
  action: string
): boolean => {
  const roles = company.roles || [];
  return roles.some((role) => roleHasPermission(role, resource, action));
};

export const resolvePermissionsForCompanyRecord = (
  company: CompanyData | null | undefined,
  resource: string
): CompanyPermissionSummary => {
  if (!company) {
    return { ...NO_ACCESS };
  }

  return {
    canRead: companyHasResourceAction(company, resource, PERMISSION_ACTIONS.READ),
    canCreate:
      companyHasResourceAction(company, resource, PERMISSION_ACTIONS.CREATE) ||
      companyHasResourceAction(company, resource, 'add'),
    canUpdate:
      companyHasResourceAction(company, resource, PERMISSION_ACTIONS.UPDATE) ||
      companyHasResourceAction(company, resource, 'change'),
    canDelete: companyHasResourceAction(company, resource, PERMISSION_ACTIONS.DELETE),
  };
};

const findCompanyById = (
  companies: CompanyData[],
  companyId: string
): CompanyData | undefined => {
  const target = normalizePermissionCompanyId(companyId);
  if (!target) return undefined;
  return companies.find((company) => normalizePermissionCompanyId(company.company_id) === target);
};

/** Aggregate read when toolbar filter is all — any company with read on resource. */
export const resolvePermissionsForAllCompanyFilter = (
  companies: CompanyData[],
  resource: string
): CompanyPermissionSummary => {
  const canRead = companies.some((company) =>
    companyHasResourceAction(company, resource, PERMISSION_ACTIONS.READ)
  );

  return {
    canRead,
    canCreate: false,
    canUpdate: false,
    canDelete: false,
  };
};

const withSummary = (
  permissions: CompanyPermissionSummary,
  effectiveCompanyId: string | null,
  isGlobalScope: boolean
): ResolveCompanyPermissionsResult => ({
  ...permissions,
  effectiveCompanyId,
  isGlobalScope,
  summary: { ...permissions },
});

/**
 * Resolve CRUD flags for a module resource scoped to a toolbar company id.
 * Pure function — safe for tests and non-React callers.
 */
export function resolveCompanyPermissions(
  input: ResolveCompanyPermissionsInput
): ResolveCompanyPermissionsResult {
  const companies = Array.isArray(input.companies) ? input.companies : [];
  const globalRoles = input.globalRoles || [];
  const isGlobalScope = hasGlobalRoleScope(
    globalRoles,
    input.selectedCompany,
    input.hasGlobalRole
  );

  if (isGlobalScope) {
    const effectiveCompanyId = isSpecificPermissionCompanyId(input.companyId)
      ? normalizePermissionCompanyId(input.companyId)
      : normalizePermissionCompanyId(input.loginCompanyId) || null;

    return withSummary(FULL_ACCESS, effectiveCompanyId, true);
  }

  const rawCompanyId = normalizePermissionCompanyId(input.companyId);

  if (!rawCompanyId || rawCompanyId === 'all') {
    const permissions = resolvePermissionsForAllCompanyFilter(companies, input.resource);
    return withSummary(permissions, null, false);
  }

  if (rawCompanyId === 'global') {
    return withSummary(FULL_ACCESS, 'global', false);
  }

  const company = findCompanyById(companies, rawCompanyId);
  const permissions = resolvePermissionsForCompanyRecord(company, input.resource);

  return withSummary(permissions, rawCompanyId, false);
}

export interface HasPermissionInCompanyInput extends ResolveCompanyPermissionsInput {
  action: string;
}

/**
 * Check a single action (CRUD or custom e.g. print/export) for a toolbar company scope.
 * Pure function — safe for tests and PermissionManager delegation.
 */
export function hasPermissionInCompany(input: HasPermissionInCompanyInput): boolean {
  const normalizedAction = String(input.action || '').trim().toLowerCase();
  if (!normalizedAction) return false;

  const resolved = resolveCompanyPermissions(input);
  if (resolved.isGlobalScope) return true;

  switch (normalizedAction) {
    case 'read':
    case 'view':
      return resolved.canRead;
    case 'create':
    case 'add':
      return resolved.canCreate;
    case 'update':
    case 'change':
      return resolved.canUpdate;
    case 'delete':
      return resolved.canDelete;
    default: {
      const companies = Array.isArray(input.companies) ? input.companies : [];
      const rawCompanyId = normalizePermissionCompanyId(input.companyId);

      if (!rawCompanyId || rawCompanyId === 'all') {
        return companies.some((company) =>
          companyHasResourceAction(company, input.resource, normalizedAction)
        );
      }

      const company = findCompanyById(companies, rawCompanyId);
      return company
        ? companyHasResourceAction(company, input.resource, normalizedAction)
        : false;
    }
  }
}

export interface ResolveFormPermissionCompanyIdInput {
  isEditMode: boolean;
  activeCompanyFilter?: string | null;
  tabCompanyId?: string | null;
  formCompanyId?: string | null;
  entityCompanyId?: string | null;
}

/**
 * Company id used to evaluate form create/update permissions.
 * Update → entity/tab company; create → toolbar or form company.
 */
export function resolveFormPermissionCompanyId(
  input: ResolveFormPermissionCompanyIdInput
): string | null {
  if (input.isEditMode) {
    const fromTab = normalizePermissionCompanyId(input.tabCompanyId);
    if (fromTab) return fromTab;

    const fromEntity = normalizePermissionCompanyId(input.entityCompanyId);
    if (fromEntity) return fromEntity;

    const fromForm = normalizePermissionCompanyId(input.formCompanyId);
    if (fromForm) return fromForm;

    return null;
  }

  const toolbar = normalizePermissionCompanyId(input.activeCompanyFilter);
  if (isSpecificPermissionCompanyId(toolbar)) {
    return toolbar;
  }

  const fromForm = normalizePermissionCompanyId(input.formCompanyId);
  return fromForm || null;
}
